/**
 * Links — partner tracking links.
 *
 * "Refined dark command-center" treatment: atmospheric glow, orchestrated
 * staggered page-load, premium stat tiles, polished data table.
 *   • Toolbar: search + status filter + Table/Cards toggle (persisted).
 *   • Bulk selection: row checkboxes → selection bar (enable / disable / delete).
 *   • Confirm dialog guards disabling & deleting (single or bulk).
 *   • Create modal: pick offer (URL auto-derived) + bulk names.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import {
  Bitcoin,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Copy,
  LayoutGrid,
  Link as LinkIcon,
  Minus,
  MousePointerClick,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Sparkles,
  Table2,
  Target,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  Label,
  Modal,
  PageHeader,
  Segmented,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents, formatDate, formatNumber } from '@/lib/format';
import type { Offer, TrackingLink } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────

// Базовый origin для трекинг-ссылок — текущий домен кабинета (в проде
// nexus-partners.click). Раньше был жёстко прописан localhost:3000.
const TRACKER_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
// Куда ведёт ссылка: AI-оффер → лендинг tema-ai.site, Crypto-оффер → крипто-бот в Telegram.
const TARGET_AI = 'https://tema-ai.site';
const TARGET_CRYPTO = 'https://t.me/temaalt_crypto_bot';
// Fallback-маппинг для офферов, у которых ещё нет landing_url в БД (legacy).
// Новые офферы — админ редактирует поле landingUrl в /offers, оно автоматически
// прилетит сюда и используется как target_url. Это словарь — только последний рубеж.
const OFFER_TARGETS: Record<string, string> = {
  'vexa-ai': 'https://vexa-ai.duckdns.org',
  'helix-code': 'https://helix-code.duckdns.org',
  scanly: 'https://scanly.duckdns.org',
};
const MAX_BULK = 25;

function isCrypto(slug: string, name: string): boolean {
  const s = `${slug} ${name}`.toLowerCase();
  return s.includes('crypto') || s.includes('крипт');
}
/**
 * Резолв URL цели для tracking-ссылки. Приоритет:
 *   1. `landingUrl` из API оффера (data-driven из affiliate_offer_packages.landing_url).
 *   2. Hardcoded `OFFER_TARGETS` (fallback пока БД не отдала / для legacy slugs).
 *   3. По названию: crypto → крипто-бот, иначе AI Lab.
 */
function targetForOffer(slug: string, name: string, landingUrl?: string | null): string {
  if (landingUrl && landingUrl.trim()) return landingUrl.trim();
  if (OFFER_TARGETS[slug]) return OFFER_TARGETS[slug];
  return isCrypto(slug, name) ? TARGET_CRYPTO : TARGET_AI;
}
function absUrl(u: string): string {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `${TRACKER_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;
}
function hostOf(u: string): string {
  try {
    return new URL(absUrl(u)).host;
  } catch {
    return u;
  }
}
function shortUrl(u: string): string {
  return u.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}
function plural(n: number, [one, few, many]: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}
function offerIcon(slug: string, name: string): LucideIcon {
  return isCrypto(slug, name) ? Bitcoin : Sparkles;
}

const rise = (ms: number): CSSProperties => ({
  animation: 'linkRise 0.55s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});
const fade = (ms: number): CSSProperties => ({
  animation: 'linkFade 0.5s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});

type ViewMode = 'table' | 'cards';
type StatusFilter = 'all' | 'active' | 'disabled';
type SortKey = 'name' | 'clicks' | 'uniqueClicks' | 'conv' | 'epc' | 'revenueCents' | 'createdAt';
type SortDir = 'asc' | 'desc';
/** Pending confirmation — covers single & bulk disable/delete. */
type ConfirmState = { kind: 'disable' | 'delete'; ids: number[]; label: string } | null;

interface PickerOffer {
  slug: string;
  name: string;
  payout: string;
  targetUrl: string;
}

function buildPickerOffers(apiOffers: Offer[]): PickerOffer[] {
  const aiLab: PickerOffer = {
    slug: 'ai-lab',
    name: 'AI Lab — Академия AI',
    payout: 'RevShare 50%',
    targetUrl: targetForOffer('ai-lab', 'AI Lab'),
  };
  // ai-rev-50 — бэкенд-запись того же оффера AI Lab; в пикере показываем
  // встроенную карточку, дубль из API убираем.
  const rest = apiOffers
    .filter((o) => o.slug !== 'ai-rev-50')
    .map<PickerOffer>((o) => ({
    slug: o.slug,
    name: o.name || o.slug,
    payout:
      o.revsharePercent != null && o.revsharePercent > 0
        ? `RevShare ${o.revsharePercent}%`
        : o.cpaCents != null && o.cpaCents > 0
          ? `CPA ${formatCents(o.cpaCents)}`
          : (o.payoutModel ?? '').toUpperCase(),
    // landingUrl приходит с бэка из affiliate_offer_packages.landing_url —
    // переопределяет hardcoded fallback. Меняется админом в /offers.
    targetUrl: targetForOffer(o.slug, o.name || '', o.landingUrl),
  }));
  return [aiLab, ...rest];
}

// ─────────────────────────────────────────────────────────────────────────
//  Checkbox
// ─────────────────────────────────────────────────────────────────────────

function Checkbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  ariaLabel?: string;
}) {
  const filled = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[var(--radius-xs)] border transition-colors"
      style={{
        borderColor: filled ? 'var(--accent)' : 'var(--border-strong)',
        backgroundColor: filled ? 'var(--accent)' : 'transparent',
        color: 'var(--accent-fg)',
      }}
    >
      {indeterminate ? (
        <Minus size={12} strokeWidth={3.5} />
      ) : checked ? (
        <Check size={12} strokeWidth={3.5} />
      ) : null}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Stat tile
// ─────────────────────────────────────────────────────────────────────────

function StatTile({
  icon: Icon,
  label,
  value,
  tint,
  style,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tint: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className="group relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] p-4 transition-[border-color,transform] duration-[var(--duration-base)] ease-[var(--ease-out)] hover:-translate-y-0.5 hover:border-[var(--border-strong)]"
      style={style}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-50 transition-opacity duration-[var(--duration-base)] group-hover:opacity-90"
        style={{ background: `radial-gradient(circle, color-mix(in oklch, ${tint} 34%, transparent), transparent 70%)` }}
      />
      <div className="relative flex items-center gap-3">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
          style={{
            backgroundColor: `color-mix(in oklch, ${tint} 15%, transparent)`,
            border: `1px solid color-mix(in oklch, ${tint} 30%, transparent)`,
            color: tint,
          }}
          aria-hidden
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
            {label}
          </div>
          <div
            className="mt-0.5 text-[22px] font-bold tabular-nums leading-none text-[var(--text)]"
            style={{ letterSpacing: 'var(--tracking-tight)' }}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div
        className="text-[16px] font-bold tabular-nums leading-none text-[var(--text)]"
        style={{ letterSpacing: 'var(--tracking-tight)' }}
      >
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
        {label}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TrackingLink['status'] }) {
  return status === 'active' ? (
    <Badge tone="success" dot>Активна</Badge>
  ) : (
    <Badge tone="neutral" dot>Отключена</Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Link card (cards view)
// ─────────────────────────────────────────────────────────────────────────

function LinkCard({
  link,
  selected,
  onSelect,
  onCopy,
  onEdit,
  onToggle,
  onDelete,
  busy,
  style,
}: {
  link: TrackingLink;
  selected: boolean;
  onSelect: () => void;
  onCopy: (url: string) => void;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  busy: boolean;
  style?: CSSProperties;
}) {
  const active = link.status === 'active';
  const full = absUrl(link.trackingUrl);

  return (
    <Card
      className={'flex flex-col p-5 ' + (selected ? 'ring-1 ring-[var(--accent)]' : '')}
      style={style}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Checkbox checked={selected} onChange={onSelect} ariaLabel="Выбрать ссылку" />
          <span
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
            style={{
              backgroundColor: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 28%, transparent)',
              color: 'var(--accent-bright)',
            }}
            aria-hidden
          >
            <LinkIcon size={17} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-[var(--text)]">
              {link.name || 'Без названия'}
            </div>
            <div className="font-mono text-[11px] text-[var(--text-quiet)]">{link.code}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <StatusBadge status={link.status} />
          <IconButton onClick={onEdit} disabled={busy} title="Изменить">
            <Pencil size={14} />
          </IconButton>
          <IconButton onClick={onToggle} disabled={busy} title={active ? 'Отключить' : 'Включить'}>
            {active ? <Pause size={14} /> : <Play size={14} />}
          </IconButton>
          <IconButton onClick={onDelete} disabled={busy} tone="danger" title="Удалить">
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onCopy(full)}
        className="group mt-4 flex w-full items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-base)]/50 px-3 py-2.5 text-left transition-colors hover:border-[var(--accent)]"
      >
        <LinkIcon size={13} strokeWidth={2} className="shrink-0 text-[var(--text-quiet)]" />
        <span className="flex-1 truncate font-mono text-[12px] text-[var(--text-secondary)]">{full}</span>
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[var(--text-muted)] transition-colors group-hover:text-[var(--accent-bright)]">
          <Copy size={12} strokeWidth={2.5} />
          Копировать
        </span>
      </button>

      <div className="mt-4 grid grid-cols-4 gap-3">
        <CardStat label="Клики" value={formatNumber(link.clicks)} />
        <CardStat label="Уник." value={formatNumber(link.uniqueClicks)} />
        <CardStat label="Конв." value={formatNumber(link.conv)} />
        <CardStat label="EPC" value={formatCents(link.epc)} />
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] pt-3 text-[11px] text-[var(--text-quiet)]">
        <span className="tabular-nums">Создана {formatDate(link.createdAt)}</span>
        <span className="truncate">ведёт на {hostOf(link.targetUrl)}</span>
      </div>
    </Card>
  );
}

function LinkCardSkeleton({ style }: { style?: CSSProperties }) {
  return (
    <Card className="p-5" style={style}>
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-[var(--radius-md)]" />
        <div className="flex-1">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="mt-2 h-3 w-1/4" />
        </div>
      </div>
      <Skeleton className="mt-4 h-10 w-full rounded-[var(--radius-md)]" />
      <div className="mt-4 grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Table view
// ─────────────────────────────────────────────────────────────────────────

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  right,
}: {
  label: string;
  col: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (c: SortKey) => void;
  right?: boolean;
}) {
  const active = sortKey === col;
  return (
    <TH className={right ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={
          'inline-flex items-center gap-1 transition-colors ' +
          (right ? 'flex-row-reverse ' : '') +
          (active ? 'text-[var(--accent-bright)]' : 'hover:text-[var(--text-secondary)]')
        }
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp size={12} strokeWidth={2.5} />
          ) : (
            <ChevronDown size={12} strokeWidth={2.5} />
          )
        ) : (
          <ChevronsUpDown size={12} className="opacity-35" />
        )}
      </button>
    </TH>
  );
}

function NumCell({ text, zero, strong }: { text: string; zero: boolean; strong?: boolean }) {
  return (
    <TD className="text-right tabular-nums">
      <span
        className={
          zero
            ? 'text-[var(--text-quiet)]'
            : strong
              ? 'font-semibold text-[var(--text)]'
              : 'text-[var(--text-secondary)]'
        }
      >
        {text}
      </span>
    </TD>
  );
}

function LinksTable({
  links,
  selectedIds,
  onToggleSelect,
  allSelected,
  someSelected,
  onToggleAll,
  sortKey,
  sortDir,
  onSort,
  onCopy,
  onEdit,
  onToggle,
  onDelete,
  busy,
  style,
}: {
  links: TrackingLink[];
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (c: SortKey) => void;
  onCopy: (url: string) => void;
  onEdit: (l: TrackingLink) => void;
  onToggle: (l: TrackingLink) => void;
  onDelete: (l: TrackingLink) => void;
  busy: boolean;
  style?: CSSProperties;
}) {
  const headProps = { sortKey, sortDir, onSort };
  return (
    <Card className="overflow-hidden" style={style}>
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{
          background:
            'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 60%, transparent) 30%, color-mix(in oklch, var(--accent) 60%, transparent) 70%, transparent)',
        }}
      />
      <div className="w-full">
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH className="w-[44px]">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={onToggleAll}
                  ariaLabel="Выбрать все"
                />
              </TH>
              <SortHeader label="Название" col="name" {...headProps} />
              <TH>Трекинг-ссылка</TH>
              <TH>Статус</TH>
              <SortHeader label="Клики" col="clicks" right {...headProps} />
              <SortHeader label="Уник." col="uniqueClicks" right {...headProps} />
              <SortHeader label="Конв." col="conv" right {...headProps} />
              <SortHeader label="EPC" col="epc" right {...headProps} />
              <SortHeader label="Доход" col="revenueCents" right {...headProps} />
              <SortHeader label="Создана" col="createdAt" {...headProps} />
              <TH className="w-[84px]" />
            </TR>
          </THead>
          <TBody>
            {links.map((l, i) => {
              const full = absUrl(l.trackingUrl);
              const active = l.status === 'active';
              const chip = active ? 'var(--accent)' : 'var(--text-muted)';
              const sel = selectedIds.has(l.id);
              return (
                <TR
                  key={l.id}
                  className="group"
                  style={{
                    ...rise(60 + Math.min(i, 14) * 32),
                    ...(sel
                      ? { backgroundColor: 'color-mix(in oklch, var(--accent) 9%, transparent)' }
                      : {}),
                  }}
                >
                  <TD>
                    <Checkbox
                      checked={sel}
                      onChange={() => onToggleSelect(l.id)}
                      ariaLabel={`Выбрать ${l.name || l.code}`}
                    />
                  </TD>
                  <TD>
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
                        style={{
                          backgroundColor: `color-mix(in oklch, ${chip} 14%, transparent)`,
                          border: `1px solid color-mix(in oklch, ${chip} 26%, transparent)`,
                          color: active ? 'var(--accent-bright)' : 'var(--text-muted)',
                        }}
                        aria-hidden
                      >
                        <LinkIcon size={15} strokeWidth={2} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-[var(--text)]">
                          {l.name || 'Без названия'}
                        </div>
                        <div className="font-mono text-[11px] text-[var(--text-quiet)]">{l.code}</div>
                      </div>
                    </div>
                  </TD>
                  <TD>
                    <button
                      type="button"
                      onClick={() => onCopy(full)}
                      title="Скопировать ссылку"
                      className="group/url inline-flex h-7 max-w-[260px] items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-base)]/60 pl-2.5 pr-1 transition-colors hover:border-[var(--accent)]"
                    >
                      <span className="truncate font-mono text-[12px] text-[var(--text-secondary)]">
                        {full}
                      </span>
                      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--radius-xs)] text-[var(--text-quiet)] transition-colors group-hover/url:text-[var(--accent-bright)]">
                        <Copy size={12} strokeWidth={2.5} />
                      </span>
                    </button>
                  </TD>
                  <TD>
                    <StatusBadge status={l.status} />
                  </TD>
                  <NumCell text={formatNumber(l.clicks)} zero={!l.clicks} strong />
                  <NumCell text={formatNumber(l.uniqueClicks)} zero={!l.uniqueClicks} />
                  <NumCell text={formatNumber(l.conv)} zero={!l.conv} strong />
                  <NumCell text={formatCents(l.epc)} zero={!l.epc} />
                  <TD className="text-right tabular-nums">
                    <span
                      className="font-semibold"
                      style={{ color: l.revenueCents > 0 ? 'var(--success)' : 'var(--text-quiet)' }}
                    >
                      {formatCents(l.revenueCents)}
                    </span>
                  </TD>
                  <TD className="whitespace-nowrap text-[12px] text-[var(--text-muted)]">
                    {formatDate(l.createdAt)}
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-0.5 opacity-65 transition-opacity group-hover:opacity-100">
                      <IconButton onClick={() => onEdit(l)} disabled={busy} title="Изменить">
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton
                        onClick={() => onToggle(l)}
                        disabled={busy}
                        title={active ? 'Отключить' : 'Включить'}
                      >
                        {active ? <Pause size={14} /> : <Play size={14} />}
                      </IconButton>
                      <IconButton
                        onClick={() => onDelete(l)}
                        disabled={busy}
                        tone="danger"
                        title="Удалить"
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  View toggle
// ─────────────────────────────────────────────────────────────────────────

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const opts: { v: ViewMode; Icon: LucideIcon; label: string }[] = [
    { v: 'table', Icon: Table2, label: 'Таблица' },
    { v: 'cards', Icon: LayoutGrid, label: 'Карточки' },
  ];
  return (
    <div className="inline-flex rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-base)]/40 p-0.5">
      {opts.map(({ v, Icon, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          title={label}
          className={
            'inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-xs)] px-2.5 text-[12px] font-medium transition-colors ' +
            (value === v
              ? 'bg-[var(--accent-soft)] text-[var(--accent-bright)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text)]')
          }
        >
          <Icon size={14} strokeWidth={2} />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  CreateLinkModal — pick offer (URL auto-derived), add one/many names
// ─────────────────────────────────────────────────────────────────────────

function CreateLinkModal({
  open,
  onClose,
  offers,
}: {
  open: boolean;
  onClose: () => void;
  offers: PickerOffer[];
}) {
  const createLink = partnerApi.links.useCreate();
  const [offerSlug, setOfferSlug] = useState('ai-lab');
  const [names, setNames] = useState<string[]>(['']);
  const [namesError, setNamesError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setOfferSlug('ai-lab');
      setNames(['']);
      setNamesError(undefined);
      setSubmitting(false);
    }
  }, [open]);

  const selectedOffer = offers.find((o) => o.slug === offerSlug) ?? offers[0];
  const validNames = names.map((n) => n.trim()).filter(Boolean);
  const count = validNames.length;

  const updateName = (i: number, v: string) => {
    setNames((arr) => arr.map((n, idx) => (idx === i ? v : n)));
    setNamesError(undefined);
  };
  const addName = () => setNames((arr) => (arr.length >= MAX_BULK ? arr : [...arr, '']));
  const removeName = (i: number) =>
    setNames((arr) => (arr.length <= 1 ? arr : arr.filter((_, idx) => idx !== i)));

  const submit = async () => {
    if (count === 0) {
      setNamesError('Добавьте хотя бы одно название');
      return;
    }
    const targetUrl = selectedOffer?.targetUrl ?? TARGET_AI;
    // offerSlug → бэк сам резолвит landing_url из affiliate_offer_packages.
    // targetUrl остаётся как fallback (для оффера без landing_url в БД).
    const offerSlug = selectedOffer?.slug ?? '';
    setSubmitting(true);
    let ok = 0;
    for (const name of validNames) {
      try {
        await createLink.mutateAsync({ name, targetUrl, offerSlug });
        ok += 1;
      } catch {
        /* keep going — count successes */
      }
    }
    setSubmitting(false);
    if (ok > 0) {
      toast.success(ok === 1 ? 'Ссылка создана' : `Создано ссылок: ${ok}`);
      onClose();
    } else {
      toast.error('Не удалось создать ссылки');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новые трекинг-ссылки"
      subtitle="Выберите оффер и добавьте источники — на каждый создастся отдельная ссылка"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-[var(--text-quiet)] tabular-nums">
            {count > 0
              ? `${count} ${plural(count, ['ссылка', 'ссылки', 'ссылок'])} к созданию`
              : 'Добавьте названия'}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="button"
              onClick={submit}
              loading={submitting}
              variant="gradient"
              disabled={count === 0}
            >
              <Plus size={15} strokeWidth={2.5} />
              Создать{count > 1 ? ` ${count}` : ''}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <Label>Оффер</Label>
          <div className="mt-1.5 space-y-2">
            {offers.map((o) => {
              const selected = o.slug === offerSlug;
              const Icon = offerIcon(o.slug, o.name);
              return (
                <button
                  key={o.slug}
                  type="button"
                  onClick={() => setOfferSlug(o.slug)}
                  className={
                    'flex w-full items-center gap-3 rounded-[var(--radius-md)] border px-3 py-2.5 text-left transition-colors ' +
                    (selected
                      ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
                      : 'border-[var(--border)] bg-[var(--bg-base)]/40 hover:border-[var(--border-strong)]')
                  }
                >
                  <span
                    className="grid h-4 w-4 shrink-0 place-items-center rounded-full border"
                    style={{ borderColor: selected ? 'var(--accent)' : 'var(--border-strong)' }}
                    aria-hidden
                  >
                    {selected && (
                      <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
                    )}
                  </span>
                  <span
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
                    style={{
                      backgroundColor: 'color-mix(in oklch, var(--success) 14%, transparent)',
                      color: 'var(--success)',
                    }}
                    aria-hidden
                  >
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-[var(--text)]">
                      {o.name}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-[var(--text-quiet)]">
                      → {shortUrl(o.targetUrl)}
                    </span>
                  </span>
                  <Badge tone="success">{o.payout}</Badge>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>Названия ссылок</Label>
            <span className="text-[11px] tabular-nums text-[var(--text-quiet)]">
              {names.length} / {MAX_BULK}
            </span>
          </div>
          <div className="mt-1.5 space-y-2">
            {names.map((name, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={name}
                  onChange={(e) => updateName(i, e.target.value)}
                  placeholder={i === 0 ? 'Например, Instagram Stories' : 'Ещё один источник трафика'}
                />
                {names.length > 1 && (
                  <IconButton type="button" onClick={() => removeName(i)} title="Убрать строку">
                    <X size={14} />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
          {namesError && <p className="mt-1.5 text-[12px] text-[var(--danger)]">{namesError}</p>}
          {names.length < MAX_BULK && (
            <button
              type="button"
              onClick={addName}
              className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent-bright)] transition-opacity hover:opacity-80"
            >
              <Plus size={13} strokeWidth={2.5} />
              Добавить ещё ссылку
            </button>
          )}
          <p className="mt-2 text-[11px] text-[var(--text-quiet)]">
            Каждое название станет отдельной трекинг-ссылкой. Адрес назначения подставляется из
            выбранного оффера автоматически.
          </p>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  EditLinkModal — rename a link (name is the only API-mutable field)
// ─────────────────────────────────────────────────────────────────────────

function EditLinkModal({ link, onClose }: { link: TrackingLink | null; onClose: () => void }) {
  const updateLink = partnerApi.links.useUpdate();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (link) {
      setName(link.name || '');
      setError(undefined);
      setSaving(false);
    }
  }, [link]);

  const save = async () => {
    if (!link) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Введите название');
      return;
    }
    if (trimmed === link.name) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateLink.mutateAsync({ id: link.id, data: { name: trimmed } });
      toast.success('Ссылка обновлена');
      onClose();
    } catch {
      /* handled globally */
    }
    setSaving(false);
  };

  return (
    <Modal
      open={link !== null}
      onClose={onClose}
      title="Изменить ссылку"
      subtitle={link ? link.code : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" variant="gradient" loading={saving} onClick={save}>
            <Check size={15} strokeWidth={2.5} />
            Сохранить
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="edit-link-name">Название</Label>
          <Input
            id="edit-link-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
            placeholder="Название ссылки"
          />
          {error && <p className="mt-1.5 text-[12px] text-[var(--danger)]">{error}</p>}
        </div>
        <div>
          <Label>Трекинг-ссылка</Label>
          <div className="mt-1.5 flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-base)]/50 px-3 py-2.5">
            <LinkIcon size={13} strokeWidth={2} className="shrink-0 text-[var(--text-quiet)]" />
            <span className="truncate font-mono text-[12px] text-[var(--text-muted)]">
              {link ? absUrl(link.trackingUrl) : ''}
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-[var(--text-quiet)]">
            Адрес и код ссылки фиксированы — меняется только название.
          </p>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

const VIEW_KEY = 'partner.links.view';

export function LinksPage() {
  const links = partnerApi.links.useList();
  const offers = partnerApi.offers.useList();
  const updateLink = partnerApi.links.useUpdate();
  const deleteLink = partnerApi.links.useDelete();

  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TrackingLink | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'table';
    return window.localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'table';
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const items = links.data?.items ?? [];
  const loading = links.isLoading;
  const isEmpty = !loading && items.length === 0;
  const busy = updateLink.isPending || deleteLink.isPending;
  const pickerOffers = buildPickerOffers(offers.data?.items ?? []);

  /** 3-state cycle: sort → reverse → off (reset to the default newest-first). */
  const onSort = (col: SortKey) => {
    const firstDir: SortDir = col === 'name' ? 'asc' : 'desc';
    if (col !== sortKey) {
      setSortKey(col);
      setSortDir(firstDir);
    } else if (sortDir === firstDir) {
      setSortDir(firstDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey('createdAt');
      setSortDir('desc');
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = items.filter((l) => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (q && !`${l.name} ${l.code}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      let d: number;
      if (sortKey === 'name') d = (a.name || '').localeCompare(b.name || '');
      else if (sortKey === 'createdAt') d = a.createdAt.localeCompare(b.createdAt);
      else d = (a[sortKey] as number) - (b[sortKey] as number);
      return sortDir === 'asc' ? d : -d;
    });
    return sorted;
  }, [items, search, statusFilter, sortKey, sortDir]);

  const totals = items.reduce(
    (a, l) => ({
      clicks: a.clicks + l.clicks,
      conv: a.conv + l.conv,
      revenue: a.revenue + l.revenueCents,
    }),
    { clicks: 0, conv: 0, revenue: 0 },
  );

  // ── Selection ──────────────────────────────────────────────────────────
  const allSelected = visible.length > 0 && visible.every((l) => selectedIds.has(l.id));
  const someSelected = visible.some((l) => selectedIds.has(l.id));
  const toggleSelect = (id: number) =>
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleSelectAll = () =>
    setSelectedIds((s) => {
      const n = new Set(s);
      if (allSelected) visible.forEach((l) => n.delete(l.id));
      else visible.forEach((l) => n.add(l.id));
      return n;
    });
  const clearSelection = () => setSelectedIds(new Set());

  // ── Actions ────────────────────────────────────────────────────────────
  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка скопирована');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };
  const enableLink = async (link: TrackingLink) => {
    try {
      await updateLink.mutateAsync({ id: link.id, data: { status: 'active' } });
      toast.success('Ссылка включена');
    } catch {
      /* handled globally */
    }
  };
  const requestToggle = (link: TrackingLink) => {
    if (link.status === 'active') {
      setConfirmState({ kind: 'disable', ids: [link.id], label: link.name || link.code });
    } else {
      void enableLink(link);
    }
  };
  const requestDelete = (link: TrackingLink) => {
    setConfirmState({ kind: 'delete', ids: [link.id], label: link.name || link.code });
  };
  const bulkEnable = async () => {
    const ids = [...selectedIds];
    let ok = 0;
    for (const id of ids) {
      try {
        await updateLink.mutateAsync({ id, data: { status: 'active' } });
        ok += 1;
      } catch {
        /* keep going */
      }
    }
    clearSelection();
    if (ok > 0) toast.success(`Включено ссылок: ${ok}`);
  };
  const runConfirm = async () => {
    if (!confirmState) return;
    const { kind, ids } = confirmState;
    setConfirmBusy(true);
    let ok = 0;
    for (const id of ids) {
      try {
        if (kind === 'disable') await updateLink.mutateAsync({ id, data: { status: 'disabled' } });
        else await deleteLink.mutateAsync(id);
        ok += 1;
      } catch {
        /* keep going */
      }
    }
    setConfirmBusy(false);
    setConfirmState(null);
    setSelectedIds((s) => {
      const n = new Set(s);
      ids.forEach((i) => n.delete(i));
      return n;
    });
    if (ok > 0) {
      toast.success(
        kind === 'disable'
          ? ok === 1
            ? 'Ссылка отключена'
            : `Отключено ссылок: ${ok}`
          : ok === 1
            ? 'Ссылка удалена'
            : `Удалено ссылок: ${ok}`,
      );
    } else {
      toast.error('Не удалось выполнить действие');
    }
  };

  const selCount = selectedIds.size;
  const cIds = confirmState?.ids ?? [];
  const cSingle = cIds.length === 1;
  const cDisable = confirmState?.kind === 'disable';

  return (
    <div className="relative mx-auto max-w-[1280px]">
      <style>{`
        @keyframes linkRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes linkFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-120px] -z-10 h-[360px] w-[760px] -translate-x-1/2"
        style={{
          background:
            'radial-gradient(ellipse at center, color-mix(in oklch, var(--accent) 20%, transparent), transparent 68%)',
          filter: 'blur(36px)',
        }}
      />

      <div style={rise(0)}>
        <PageHeader
          eyebrow="Партнёрская программа"
          title="Трекинг-ссылки"
          subtitle="Создавайте ссылки под каждый источник — клики и конверсии считаются автоматически."
          actions={
            !isEmpty ? (
              <Button onClick={() => setOpen(true)} variant="gradient">
                <Plus size={15} strokeWidth={2.5} />
                Создать ссылку
              </Button>
            ) : undefined
          }
        />
      </div>

      {!loading && items.length > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile icon={LinkIcon} label="Ссылок" value={formatNumber(items.length)} tint="var(--accent-bright)" style={rise(70)} />
          <StatTile icon={MousePointerClick} label="Всего кликов" value={formatNumber(totals.clicks)} tint="var(--info)" style={rise(125)} />
          <StatTile icon={Target} label="Конверсии" value={formatNumber(totals.conv)} tint="var(--success)" style={rise(180)} />
          <StatTile icon={Wallet} label="Доход" value={formatCents(totals.revenue)} tint="var(--warning)" style={rise(235)} />
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3" style={rise(300)}>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-quiet)]"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по названию или коду"
                className="w-[240px] pl-9"
              />
            </div>
            <Segmented<StatusFilter>
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'active', label: 'Активные' },
                { value: 'disabled', label: 'Отключённые' },
              ]}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[12px] tabular-nums text-[var(--text-quiet)]">
              {visible.length === items.length
                ? `${items.length} ${plural(items.length, ['ссылка', 'ссылки', 'ссылок'])}`
                : `Найдено: ${visible.length}`}
            </span>
            <ViewToggle value={view} onChange={setView} />
          </div>
        </div>
      )}

      {/* Bulk selection bar */}
      {selCount > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-lg)] border px-4 py-2.5"
          style={{
            ...rise(0),
            borderColor: 'color-mix(in oklch, var(--accent) 40%, var(--border))',
            backgroundColor: 'color-mix(in oklch, var(--accent) 9%, var(--bg-raised))',
          }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-fg)' }}
              aria-hidden
            >
              <Check size={13} strokeWidth={3} />
            </span>
            <span className="text-[13px] font-semibold text-[var(--text)] tabular-nums">
              {selCount} {plural(selCount, ['ссылка', 'ссылки', 'ссылок'])} выбрано
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={bulkEnable} disabled={busy}>
              <Play size={13} strokeWidth={2.5} />
              Включить
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirmState({ kind: 'disable', ids: [...selectedIds], label: '' })}
            >
              <Pause size={13} strokeWidth={2.5} />
              Отключить
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={() => setConfirmState({ kind: 'delete', ids: [...selectedIds], label: '' })}
            >
              <Trash2 size={13} strokeWidth={2.5} />
              Удалить
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X size={13} strokeWidth={2.5} />
              Снять
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <LinkCardSkeleton key={i} style={fade(60 + i * 70)} />
          ))}
        </div>
      )}

      {isEmpty && (
        <Card style={fade(80)}>
          <EmptyState
            icon={<LinkIcon size={22} />}
            title="Ссылок ещё нет"
            description="Создайте первую трекинг-ссылку — и здесь появится статистика по кликам и конверсиям."
            action={
              <Button onClick={() => setOpen(true)} variant="gradient" size="lg">
                <Plus size={15} strokeWidth={2.5} />
                Создать первую ссылку
              </Button>
            }
          />
        </Card>
      )}

      {!loading && items.length > 0 && visible.length === 0 && (
        <Card style={fade(80)}>
          <EmptyState
            icon={<Search size={22} />}
            title="Ничего не найдено"
            description="Под текущие фильтры не подошла ни одна ссылка. Измените поиск или статус."
          />
        </Card>
      )}

      {!loading && visible.length > 0 && view === 'table' && (
        <LinksTable
          links={visible}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleAll={toggleSelectAll}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          onCopy={copyUrl}
          onEdit={setEditTarget}
          onToggle={requestToggle}
          onDelete={requestDelete}
          busy={busy}
          style={fade(360)}
        />
      )}

      {!loading && visible.length > 0 && view === 'cards' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {visible.map((l, i) => (
            <LinkCard
              key={l.id}
              link={l}
              selected={selectedIds.has(l.id)}
              onSelect={() => toggleSelect(l.id)}
              busy={busy}
              onCopy={copyUrl}
              onEdit={() => setEditTarget(l)}
              onToggle={() => requestToggle(l)}
              onDelete={() => requestDelete(l)}
              style={rise(360 + Math.min(i, 14) * 40)}
            />
          ))}
        </div>
      )}

      <CreateLinkModal open={open} onClose={() => setOpen(false)} offers={pickerOffers} />

      <EditLinkModal link={editTarget} onClose={() => setEditTarget(null)} />

      {/* Confirm — guards disabling & deleting (single or bulk) */}
      <Modal
        open={confirmState !== null}
        onClose={() => setConfirmState(null)}
        title={
          cDisable
            ? cSingle
              ? 'Отключить ссылку?'
              : `Отключить ${cIds.length} ${plural(cIds.length, ['ссылку', 'ссылки', 'ссылок'])}?`
            : cSingle
              ? 'Удалить ссылку?'
              : `Удалить ${cIds.length} ${plural(cIds.length, ['ссылку', 'ссылки', 'ссылок'])}?`
        }
        subtitle={cSingle && confirmState?.label ? `«${confirmState.label}»` : undefined}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setConfirmState(null)}>
              Отмена
            </Button>
            <Button type="button" variant="danger" loading={confirmBusy} onClick={runConfirm}>
              {cDisable ? <Pause size={15} strokeWidth={2.5} /> : <Trash2 size={15} strokeWidth={2.5} />}
              {cDisable ? 'Отключить' : 'Удалить'}
            </Button>
          </div>
        }
      >
        <div className="flex gap-3">
          <span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
            style={{
              backgroundColor: `color-mix(in oklch, ${cDisable ? 'var(--warning)' : 'var(--danger)'} 15%, transparent)`,
              border: `1px solid color-mix(in oklch, ${cDisable ? 'var(--warning)' : 'var(--danger)'} 30%, transparent)`,
              color: cDisable ? 'var(--warning)' : 'var(--danger)',
            }}
            aria-hidden
          >
            {cDisable ? <Pause size={16} strokeWidth={2.5} /> : <Trash2 size={16} strokeWidth={2.5} />}
          </span>
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
            {cDisable
              ? 'Отключённые ссылки перестанут принимать клики и конверсии. Накопленная статистика сохранится — включить обратно можно в любой момент.'
              : 'Действие необратимо: выбранные ссылки и вся их статистика будут удалены безвозвратно.'}
          </p>
        </div>
      </Modal>
    </div>
  );
}
